// ハルシネーション検知 S2「検知実行」の API リクエスト関数群。
// パス: /spaces/{spaceId}/services/{serviceId}/hallucination/...（BASE_URL に /api を含むため /spaces から）。
// 応答/入力の型は @/types/api/hallucination-detection（BE block2 design §7 契約）。
// HTTP ステータス判定は S1 と共通の getHallucinationErrorStatus を再利用する。

import { apiFetcher } from '@/api/api-fetcher'
import { logger } from '@/utils/logger'
import type {
  DetectionProgressResponse,
  DetectionJobListResponse,
  DetectionReadinessResponse,
  DetectionResultResponse,
  DetectionResultZipResponse,
  DetectionStatusResponse,
  ListDetectionJobsParams,
  StartDetectionRequest,
  StartDetectionResponse,
} from '@/types/api/hallucination-detection'

export { getHallucinationErrorStatus } from '@/api/requests/hallucination'

function basePath(spaceId: string, serviceId: string): string {
  return `/spaces/${spaceId}/services/${serviceId}/hallucination`
}

/**
 * 検知受付（202）。400＝入力上限違反（combination>100・モデル0/11+・未知モデル）、429＝同時実行超過、
 * 404＝space/service 不正（存在隠蔽）、403＝機能無効。
 */
export async function startHallucinationDetection(
  spaceId: string,
  serviceId: string,
  request: StartDetectionRequest,
  signal?: AbortSignal
): Promise<StartDetectionResponse> {
  const response = await apiFetcher.post<StartDetectionResponse>(
    `${basePath(spaceId, serviceId)}/jobs`,
    request,
    { signal }
  )
  logger.debug('startHallucinationDetection response:', response)
  return response
}

/** 検知ステータス（30秒ポーリング・200／404）。 */
export async function getHallucinationDetectionStatus(
  spaceId: string,
  serviceId: string,
  executionId: string,
  signal?: AbortSignal
): Promise<DetectionStatusResponse> {
  return apiFetcher.get<DetectionStatusResponse>(
    `${basePath(spaceId, serviceId)}/jobs/${executionId}`,
    { signal }
  )
}

/** 検知準備チェック（S2 前提ゲート・200）。検索可能な正解データ（ACTIVE チャンク）の有無を返す。 */
export async function getHallucinationDetectionReadiness(
  spaceId: string,
  serviceId: string,
  signal?: AbortSignal
): Promise<DetectionReadinessResponse> {
  return apiFetcher.get<DetectionReadinessResponse>(
    `${basePath(spaceId, serviceId)}/detection/readiness`,
    { signal }
  )
}

/** S4 検知結果詳細（4階層read・成功系のみ本体・200／404）。非成功系は combinations 空で status のみ返る。 */
export async function getHallucinationDetectionResult(
  spaceId: string,
  serviceId: string,
  executionId: string,
  signal?: AbortSignal
): Promise<DetectionResultResponse> {
  return apiFetcher.get<DetectionResultResponse>(
    `${basePath(spaceId, serviceId)}/jobs/${executionId}/result`,
    { signal }
  )
}

/**
 * 検知結果CSV/ZIP取得（S3 行アクション・block3 §6/§9）。GCS署名付きURL（15分）を JSON で受け取り、
 * FE が署名URLへアンカー遷移してダウンロードする（分析Export と同契約）。
 * 200＝成功系・400＝非成功系（未終端/失敗）・404＝space/service 不正（存在隠蔽）・403＝機能無効。
 * `lang` は CSV ヘッダー言語（既存ユーザー言語設定を渡す・BE 側で未設定/不正は ja フォールバック・T5）。
 */
export async function exportHallucinationDetectionResult(
  spaceId: string,
  serviceId: string,
  executionId: string,
  lang: 'ja' | 'en',
  signal?: AbortSignal
): Promise<DetectionResultZipResponse> {
  return apiFetcher.get<DetectionResultZipResponse>(
    `${basePath(spaceId, serviceId)}/jobs/${executionId}/result.zip`,
    { params: { lang }, signal }
  )
}

/** S3 検知ステータス一覧（軽量read・日時降順・200／404）。page/pageSize/status は任意（BE 側で既定 1/20）。 */
export async function listHallucinationDetectionJobs(
  spaceId: string,
  serviceId: string,
  params?: ListDetectionJobsParams,
  signal?: AbortSignal
): Promise<DetectionJobListResponse> {
  const query: Record<string, unknown> = {}
  if (params?.page != null) query.page = params.page
  if (params?.pageSize != null) query.pageSize = params.pageSize
  if (params?.status) query.status = params.status
  return apiFetcher.get<DetectionJobListResponse>(`${basePath(spaceId, serviceId)}/jobs`, {
    params: query,
    signal,
  })
}

/** 進行中の検知実行数（FR-16-5・インジケーター①）。 */
export async function getHallucinationDetectionProgress(
  spaceId: string,
  serviceId: string,
  signal?: AbortSignal
): Promise<DetectionProgressResponse> {
  return apiFetcher.get<DetectionProgressResponse>(
    `${basePath(spaceId, serviceId)}/detection/progress`,
    { signal }
  )
}
